<template>
  <v-card :width="width" flat tile color="grey lighten-5">
    <v-card-title>
      <v-avatar tile class="mr-3">
        <font-awesome-icon color="grey" :icon="app.more ? app.more.icon : 'fas fa-code-branch'" />
      </v-avatar>
      <v-tooltip :color="status.color + ' lighten-5'" right>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-badge inline dot :color="status.type">
              <div style="font-family: monospace; overflow: hidden; white-space: nowrap; max-width: 250px;" class="font-weight-bold pt-1">{{ app.repository.split('/')[1] }}</div>
            </v-badge>
          </div>
        </template>
        <span :class="status.color + '--text'">{{ status.text }}</span>
      </v-tooltip>
      <v-spacer />
      <v-icon v-if="!app.bug">hourglass_empty</v-icon>
      <v-tooltip v-else left :color="app.bug.errors > 10 ? 'error' : (app.bug.errors > 0 ? 'warning' : 'success')">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-badge
              overline
              left
              inline
              :color="app.bug.errors > 10 ? 'error' : (app.bug.errors > 0 ? 'warning' : 'success')"
              :content="app.bug.errors.toString()">
              <v-btn large icon @click="bug()">
                <v-icon>bug_report</v-icon>
              </v-btn>
            </v-badge>
          </div>
        </template>
        <span>{{ app.bug.errors }} erro(s) em {{ app.bug.issues }} issue(s)</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text style="background-color: white;" v-if="[ 'CREATING', 'ACTIVATING', 'DEACTIVATING' ].includes(app.status)">
      <v-img src="@/assets/wait.png" height="150" contain />
    </v-card-text>
    <v-card-text class="text-center py-6" style="background-color: white;" v-if="[ 'DEACTIVATED', 'UNDEFINED' ].includes(app.status)">
      <v-icon size="48px" color="error lighten-3" class="my-8">dangerous</v-icon>
    </v-card-text>
    <!--
    status de stages:
    DRAFT
    VALIDATING
    INVALID
    VALID
    DEPLOYING
    ACTIVE
    UNDEPLOYING
    INACTIVATED
    -->
    <v-list dense v-if="app.status === 'ACTIVE'" class="py-0">
      <!-- Linguagens do repositório (SonarQube, via /code/:project), como no Dashboard. -->
      <div class="mb-2" style="background-color: #FFF; display: block; height: 8px; overflow: hidden;">
        <template v-if="app.code && app.code.languages && app.code.languages.length !== 0">
          <v-tooltip v-for="l in app.code.languages" :key="l.unix" :color="color(l.unix)" top>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" :style="'background-color: ' + color(l.unix) + '75; width: ' + l.proportion + '%; height: 8px; float: left; cursor: help;'"></div>
            </template>
            <span>{{ l.label }} ({{ l.lines }} linhas)</span>
          </v-tooltip>
        </template>
      </div>
      <v-list-item v-for="(b, index) in ['release', 'beta', 'alpha']" :key="index" class="pl-2">
        <v-tooltip top attach>
          <template v-slot:activator="{ on, attrs }">
            <v-avatar v-bind="attrs" v-on="on" :color="stageStatus[app.stages[b].status].color + ' lighten-5'" size="28" rounded class="ml-1 mr-2" :style="interruptible(b) ? 'cursor: pointer;' : ''" @click="interruptible(b) && invalidate(b)"><v-icon small :color="stageStatus[app.stages[b].status].color">{{ stageStatus[app.stages[b].status].icon }}</v-icon></v-avatar>
          </template>
          <span>{{ stageStatus[app.stages[b].status].label }}{{ interruptible(b) ? ' (clique para interromper a validação)' : '' }}</span>
        </v-tooltip>
        <v-list-item-content>
          <v-list-item-title class="hidden-sm-and-down overline py-2">{{ b }}</v-list-item-title>
          <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
          <v-list-item-title class="hidden-md-and-up py-2" style="font-size: 18px;" v-html="stages[b].greek"></v-list-item-title>
        </v-list-item-content>
        <v-list-item-action v-if="Array.isArray(app.stages[b].deploy) && app.stages[b].deploy.length > 0">
          <v-list-item-action-text>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <!-- Versão vinda de um Releaser (servidor da equipe): laranja com ícone de servidor; cinza quando o Releaser parou
                     de reportar. O menu continua com as versões anteriores do estágio. -->
                <v-chip v-bind="attrs" v-on="on" dark label small :color="app.stages[b].releaser ? (app.stages[b].releaser.stale ? 'grey' : 'orange darken-3') : stages[b].color" class="text-uppercase font-weight-medium" close close-icon="keyboard_arrow_down">
                  <v-icon class="mr-2" small>{{ app.stages[b].releaser ? 'dns' : 'rocket_launch' }}</v-icon>
                  {{ app.stages[b].deploy[0] }}
                </v-chip>
              </template>
              <v-list dense>
                <v-list-item v-if="app.stages[b].releaser" class="grey lighten-4">
                  <v-list-item-subtitle class="text-wrap orange--text text--darken-3" style="white-space: pre-line; line-height: 1.5;">{{ releaserTooltip(b) }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-for="(version, index) in app.stages[b].deploy" :key="index">
                  <v-list-item-title>{{ version }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-list-item-action-text>
        </v-list-item-action>
        <v-list-item-icon>
          <v-btn icon @click="inspect(b)" :disabled="app.stages[b].deploy.length === 0 || !isAdmin || [ 'VALIDATING', 'DEPLOYING', 'UNDEPLOYING', 'REMOVING' ].includes(app.stages[b].status)"><v-icon>settings</v-icon></v-btn>
          <v-btn v-if="app.stages[b].deploy.length === 0" icon disabled><v-icon>monitor_heart</v-icon></v-btn>
          <!-- O coração junta as duas leituras: saúde dos serviços (health check) e CVEs (Trivy). -->
          <v-tooltip v-else top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" :disabled="[ 'UNDEPLOYING', 'INACTIVATED', 'REMOVING' ].includes(app.stages[b].status)" @click="health(b)">
                <v-icon :color="heart(b).color">{{ heart(b).icon }}</v-icon>
              </v-btn>
            </template>
            <span style="white-space: pre-line;">{{ heart(b).text }}</span>
          </v-tooltip>
          <v-menu>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon :disabled="app.stages[b].links.length === 0 || app.stages[b].status === 'INACTIVATED'" v-bind="attrs" v-on="on"><v-icon>link</v-icon></v-btn>
            </template>
            <v-list max-width="450px">
              <div v-for="(link, sindex) in app.stages[b].links" :key="link.port">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title><a :href="'http://' + link.permanent" target="_blank" style="text-decoration: none;">{{ link.permanent }}</a></v-list-item-title>
                    <v-list-item-subtitle v-if="link.subdomain && checkUrl(link.subdomain)">
                      <i>ou</i> <a :href="link.subdomain" target="_blank" style="text-decoration: none;">{{ link.subdomain }}</a>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="link.alias && checkUrl(link.alias)">
                      <i>ou</i> <a :href="link.alias" target="_blank" style="text-decoration: none;">{{ link.alias }}</a>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="link.subpath && checkUrl(link.subpath)">
                      <i>ou</i> <a :href="link.subpath" target="_blank" style="text-decoration: none;">{{ link.subpath }}</a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-icon><v-icon color="success" x-large>public</v-icon></v-list-item-icon>
                </v-list-item>
                <v-divider v-if="sindex < app.stages[b].links.length - 1" :key="sindex" />
              </div>
            </v-list>
          </v-menu>
        </v-list-item-icon>
      </v-list-item>
    </v-list>

    <v-dialog v-model="wizard" max-width="800px" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
      <wizard-environment-wrapper @close="wizard = false" v-on="$listeners" :project="project" :app="app" :env="environment" :boilerplates="boilerplates" :clusters="clusters" :types="types" />
    </v-dialog>

    <build-inspect-dialog ref="inspect" v-on="$listeners" :boilerplates="boilerplates" :clusters="clusters" :types="types" />
    <build-health-dialog ref="health" v-on="$listeners" />

    <bug-dialog ref="bug" v-on="$listeners" />

    <confirm-wrapper ref="confirm" />
  </v-card>
</template>

<script>
import axios from 'axios'

import ErrorHelper from '@/helpers/error'

import ConfirmWrapper from '@/components/ConfirmDialog.vue'
import WizardEnvironmentWrapper from '@/components/WizardEnvironment'
import BuildInspectDialog from '@/components/BuildInspect'
import BuildHealthDialog from '@/components/BuildHealth'
import BugDialog from '@/components/AppBug'

import UtilHelper from '@/helpers/util.js'
import stc from 'string-to-color'

export default {
  mixins: [
    UtilHelper,
    ErrorHelper
  ],
  props: {
    project: {
      type: Object,
      require: true,
      default: () => {
        return {}
      }
    },
    app: {
      type: Object,
      require: true,
      default: () => {
        return {}
      }
    },
    width: {
      type: String,
      default: undefined
    },
    boilerplates: {
      type: Array,
      require: true,
      default: () => {
        return []
      }
    },
    clusters: {
      type: Object,
      require: true,
      default: () => {
        return {}
      }
    },
    types: {
      type: Array,
      require: true,
      default: () => {
        return []
      }
    }
  },
  components: {
    ConfirmWrapper,
    WizardEnvironmentWrapper,
    BuildHealthDialog,
    BuildInspectDialog,
    BugDialog
  },
  data: () => ({
    wizard: false,
    environment: null,
    status: {
      icon: 'hourglass_empty',
      type: 'warning',
      text: 'Carregando...'
    },
    stageStatus: {
      DRAFT: { icon: 'edit', color: 'blue', label: 'Rascunho' },
      VALIDATING: { icon: 'rule', color: 'orange', label: 'Validando' },
      INVALID: { icon: 'warning', color: 'red', label: 'Inválido' },
      VALID: { icon: 'verified', color: 'teal', label: 'Válido' },
      DEPLOYING: { icon: 'cloud_upload', color: 'purple', label: 'Instanciando' },
      ACTIVE: { icon: 'cloud_done', color: 'green', label: 'Ativo' },
      UNDEPLOYING: { icon: 'cloud_download', color: 'deep-orange', label: 'Desinstanciando' },
      INACTIVATED: { icon: 'public_off', color: 'blue-grey', label: 'Inativo' },
      REMOVING: { icon: 'auto_delete', color: 'red', label: 'Removendo' }
    },
    stages: {
      alpha: { greek: '&alpha;', color: 'cyan darken-1' },
      beta: { greek: '&beta;', color: 'cyan darken-2' },
      release: { greek: '&rho;', color: 'cyan darken-3' }
    }
  }),
  beforeMount () {
    this.type()
  },
  watch: {
    app: {
      handler () {
        this.type()
      },
      deep: true
    }
  },
  methods: {
    color (text) {
      return stc(text)
    },
    // Ícone, cor e texto do coração de um estágio a partir da saúde dos serviços e das CVEs.
    heart (env) {
      const stage = this.app.stages[env]

      const health = stage.health || null
      const cve = stage.cve || null

      if (!health && !cve) {
        return { icon: 'hourglass_top', color: undefined, text: 'Aguardando a verificação de saúde e a varredura de CVEs...' }
      }

      const parts = []

      if (health) {
        // ▲ saudável, ▼ com problema, ↻ iniciando
        const detail = []

        if (health.healthy) detail.push(health.healthy + ' ▲')
        if (health.unhealthy) detail.push(health.unhealthy + ' ▼')
        if (health.starting) detail.push(health.starting + ' ↻')

        parts.push(health.total + (health.total === 1 ? ' serviço' : ' serviços') + ' (' + detail.join(', ') + ')')
      } else {
        parts.push('Serviços aguardando verificação')
      }

      if (cve) {
        const total = parseInt(cve.critical) + parseInt(cve.high)

        parts.push(total ? 'CVEs: ' + parseInt(cve.critical) + ' CRITICAL e ' + parseInt(cve.high) + ' HIGH' : 'CVEs: nenhum CRITICAL ou HIGH na última varredura')
      } else {
        parts.push('CVEs: em análise, aguarde')
      }

      const text = parts.join('\n')

      if ((health && health.unhealthy > 0) || (cve && parseInt(cve.critical) > 0)) return { icon: 'heart_broken', color: 'error', text }

      if ((health && health.starting > 0) || (cve && parseInt(cve.high) > 0)) return { icon: 'heart_broken', color: 'warning', text }

      return { icon: 'monitor_heart', color: undefined, text }
    },
    // Data compacta no fuso de Brasília: 16/10/26 17:02.
    compactDate (iso) {
      if (!iso) return '?'

      const p = {}

      new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Sao_Paulo' })
        .formatToParts(new Date(iso)).forEach(x => { p[x.type] = x.value })

      return p.day + '/' + p.month + '/' + p.year + ' ' + p.hour + ':' + p.minute
    },
    releaserTooltip (env) {
      const r = this.app.stages[env].releaser

      if (!r) return ''

      return (r.stale ? 'Sem contato com o Releaser em:' : 'Implantado pelo Releaser em:') + '\n' + r.server + '\nÚltimo contato em ' + this.compactDate(r.seen)
    },
    type () {
      switch (this.app.status) {
        case 'ACTIVE':
          this.status.icon = 'done'
          this.status.type = 'success'
          this.status.text = 'Ativo!'
          this.status.color = 'green'

          break

        case 'DEACTIVATED':
          this.status.icon = 'close'
          this.status.type = 'error'
          this.status.text = 'App desativado!'
          this.status.color = 'red'

          break

        case 'CREATING':
          this.status.icon = 'construction'
          this.status.type = 'warning'
          this.status.text = 'Criando... por favor, aguarde!'
          this.status.color = 'orange'

          break

        case 'ACTIVATING':
          this.status.icon = 'file_upload'
          this.status.type = 'warning'
          this.status.text = 'Ativando... por favor, aguarde!'
          this.status.color = 'orange'

          break

        case 'DEACTIVATING':
          this.status.icon = 'file_download'
          this.status.type = 'error'
          this.status.text = 'Desativando... por favor, aguarde!'
          this.status.color = 'red'

          break

        case 'UNDEFINED':
        default:
          this.status.icon = 'error'
          this.status.type = 'error'
          this.status.text = 'Erro! Impossível obter o status.'
          this.status.color = 'red'
      }
    },
    settings (env) {
      this.environment = env

      this.wizard = true
    },
    health (env) {
      this.$refs.health.open(this.project, this.app, env)
    },
    inspect (env) {
      this.$refs.inspect.open(this.project, this.app, env)
    },
    bug () {
      this.$refs.bug.open(this.app)
    },
    // Build presa em VALIDATING (o deployer repete a tentativa e o e-mail de erro a cada minuto): o administrador a passa
    // a INVALID, inclusive em projeto arquivado, cuja equipe virou só io@embrapa.br.
    interruptible (env) {
      return this.isAdmin && this.app.stages[env].status === 'VALIDATING'
    },
    invalidate (env) {
      const build = this.app.repository + '@' + env

      this.$refs.confirm.open(
        'Interromper a validação?',
        'A build "' + build + '" deixa de ser validada e passa ao estado "Inválido", e o deployer para de tentar de novo (e de enviar o e-mail de erro). A configuração é mantida.',
        { color: 'error', width: 500, persistent: true }).then((confirm) => {
        if (!confirm) return

        const headers = { Authorization: 'Bearer ' + this.$localStorage.get('user').token }

        axios.post(process.env.VUE_APP_API + '/build/invalidate/' + this.app.repository + '/' + env, {}, { headers }).then(() => {
          this.$emit('message', 'Validação da build ' + build + ' interrompida.', 'success')

          this.$emit('refresh')
        }).catch(error => {
          this.$emit('message', this.errorMessage(error), 'error')
        })
      })
    }
  },
  computed: {
    isAdmin () {
      return this.$localStorage.get('user').role === 'Administrator'
    }
  }
}
</script>
